import { BrandIcon, BrandIconId, MaterialIcon } from '@ampcore/adel-react'
import { AmpcoreMaterialIconId } from '@ampcore/ampcom-contracts'
import { FaceProfileData } from '@ampcore/client-side-services'
import { sanitizeUrl } from '@ampcore/string-format'
import React from 'react'
import xss from 'xss'

import { rteContentFilter } from '../../lib/helpers/rteContentFilter'

export type DynamicAdvisorClientProps = {
  faceProfileData: FaceProfileData
  clientLabels: {
    advisor?: string
    refer?: string
  }
  disclosure?: string
  testId?: string
}

type ReferralLinkProps = {
  faceProfileData: DynamicAdvisorClientProps['faceProfileData']
  clientLabels: DynamicAdvisorClientProps['clientLabels']
  testId?: string
}

function ReferralLink({
  faceProfileData,
  clientLabels,
  testId
}: ReferralLinkProps) {
  const referLink =
    faceProfileData.type === 'team'
      ? faceProfileData.urls.site
      : faceProfileData.urls.referme
  const link = (
    <a
      className="Button Button--secondary Button--secondary--action-blue u-marginTop20 u-md-sizeFull u-sm-sizeFull"
      href={sanitizeUrl(xss(referLink ?? ''))}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId ? `${testId}-referral-link` : undefined}
    >
      {xss(clientLabels.refer ?? '')}
    </a>
  )

  return referLink && clientLabels.refer ? link : null
}

export const DynamicAdvisorClient: React.FC<
  DynamicAdvisorClientProps
> = props => {
  const { faceProfileData, clientLabels, disclosure, testId } = props
  const iconMap: Record<'twitter' | 'facebook' | 'linkedin', BrandIconId> = {
    twitter: BrandIconId.SOCIAL_X,
    facebook: BrandIconId.SOCIAL_FACEBOOK,
    linkedin: BrandIconId.SOCIAL_LINKEDIN
  }

  return (
    <div
      className="DynamicAdvisor DynamicAdvisor--clientView u-bgSizeCover u-bgColorMidnightBlue"
      style={{
        backgroundImage: `url(${sanitizeUrl(
          window.AMPCOMPATH + 'images/facets/hero-facets-dark-midnight-blue.png'
        )})`
      }}
      data-analytics-container="Advisor"
      data-testid={testId}
    >
      <div className="u-sizeConstrained u-paddingVert32">
        <div className="Card">
          <div className="Grid Card-body u-padding20">
            <div className="u-size8of12 u-flex u-flexRow u-paddingRight20 u-md-sizeFull u-sm-sizeFull u-sm-flexCol u-md-paddingRight0 u-sm-paddingRight0">
              <div className="DynamicAdvisor-imageWrapper u-marginRight20 u-sm-marginRight0 u-sm-marginBottom20">
                <img
                  src={sanitizeUrl(
                    xss(
                      faceProfileData.image ||
                        `${window.AMPCOMPATH}images/dac-missing-image.png`
                    )
                  )}
                  alt={xss(faceProfileData.name ?? '') || 'Missing Image'}
                  className="Advisor-image u-maxWidth100"
                  data-testid={testId ? `${testId}-image` : undefined}
                />
              </div>
              <div className="DynamicAdvisor-advisorInfo">
                <h5 className="DynamicAdvisor-heading u-textUppercase u-textColorMidnightBlue">
                  {xss(clientLabels.advisor ?? '')}
                </h5>
                <h3
                  className={
                    'AdvisorClient-name u-textSecondary u-marginBottom4 u-inlineBlock'
                  }
                  data-testid={testId ? `${testId}-name` : undefined}
                >
                  {xss(faceProfileData.name ?? '')}
                </h3>
                {faceProfileData.designations && (
                  <p
                    className="u-md-marginVert8 u-sm-marginVert8"
                    data-testid={testId ? `${testId}-designations` : undefined}
                  >
                    {xss(faceProfileData.designations)}
                  </p>
                )}
                {faceProfileData.title && (
                  <p
                    dangerouslySetInnerHTML={{
                      __html: rteContentFilter(faceProfileData.title)
                    }}
                    data-testid={testId ? `${testId}-title` : undefined}
                  ></p>
                )}
                <div className="u-md-hidden u-sm-hidden">
                  <ReferralLink
                    faceProfileData={faceProfileData}
                    clientLabels={clientLabels}
                    testId={testId}
                  />
                </div>
              </div>
            </div>
            <div className="u-lg-hidden u-size12of12">
              <ReferralLink
                faceProfileData={faceProfileData}
                clientLabels={clientLabels}
                testId={testId}
              />
            </div>
            <div className="u-size4of12 u-paddingLeft20 u-borderLeft u-md-sizeFull u-md-paddingLeft0 u-md-paddingTop20 u-md-borderLeft0 u-md-marginTop20 u-md-borderTop u-sm-sizeFull u-sm-paddingLeft0 u-sm-paddingTop20 u-sm-marginTop20 u-sm-borderLeft0 u-sm-borderTop">
              <div className="u-textPrimary u-textColorNeutralGrey u-textBold u-marginBottom8">
                Contact
              </div>
              <ul className="DynamicAdvisor-contactInfo List Grid">
                {faceProfileData.email && (
                  <li className="List-item u-sizeFull u-flex u-flexAlignItemsCenter u-padding0 u-marginBottom4 u-md-size6of12 u-sm-marginBottom8">
                    <MaterialIcon
                      icon={AmpcoreMaterialIconId.MAIL}
                      className="u-textColorActionBlue u-marginRight8"
                    />
                    <a
                      className="u-textTruncate"
                      href={sanitizeUrl(`mailto:${xss(faceProfileData.email)}`)}
                      data-testid={testId ? `${testId}-email-link` : undefined}
                    >
                      {xss(faceProfileData.email)}
                    </a>
                  </li>
                )}
                {faceProfileData.phone && (
                  <li className="List-item u-sizeFull u-flex u-flexAlignItemsCenter u-padding0 u-marginBottom4 u-md-size6of12 u-sm-marginBottom8">
                    <MaterialIcon
                      icon={AmpcoreMaterialIconId.PHONE}
                      className="u-textColorActionBlue u-marginRight8"
                    />
                    <a
                      href={sanitizeUrl(`tel:${xss(faceProfileData.phone)}`)}
                      data-testid={testId ? `${testId}-phone-link` : undefined}
                    >
                      {xss(faceProfileData.phone)}
                    </a>
                  </li>
                )}
                {faceProfileData.urls.site && (
                  <li className="List-item u-sizeFull u-flex u-flexAlignItemsCenter u-padding0 u-marginBottom4 u-md-size6of12 u-sm-marginBottom8">
                    <MaterialIcon
                      icon={AmpcoreMaterialIconId.NEW_WINDOW}
                      className="u-textColorActionBlue u-marginRight8"
                    />
                    <a
                      href={sanitizeUrl(xss(faceProfileData.urls.site))}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={
                        testId ? `${testId}-website-link` : undefined
                      }
                    >
                      Advisor website
                    </a>
                  </li>
                )}
                <li className="List-item u-sizeFull u-md-size6of12">
                  {(() => {
                    const icons = (
                      Object.keys(iconMap) as Array<keyof typeof iconMap>
                    )
                      .map(key =>
                        faceProfileData.social[key] ? (
                          <li
                            key={key}
                            className="u-textSizeMsrp1 u-flexJustifyCenter u-inlineBlock u-marginRight8"
                          >
                            <a
                              href={sanitizeUrl(
                                xss(faceProfileData.social[key] ?? '')
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="DynamicAdvisor-socialIcon Icon-link u-inlineBlock"
                              data-testid={
                                testId ? `${testId}-social-${key}` : undefined
                              }
                            >
                              <BrandIcon
                                icon={iconMap[key]}
                                ariaLabel={key}
                                style={{ width: '20px', height: '20px' }}
                              />
                            </a>
                          </li>
                        ) : null
                      )
                      .filter(Boolean)
                    return icons.length > 0 ? (
                      <ul className="u-border0">{icons}</ul>
                    ) : null
                  })()}
                </li>
              </ul>
            </div>
          </div>
        </div>
        {disclosure && (
          <div
            className="DynamicAdvisor-disclosure Content--reversed u-textSizeMsrm2 u-marginTop8"
            dangerouslySetInnerHTML={{ __html: rteContentFilter(disclosure) }}
            data-testid={testId ? `${testId}-disclosure` : undefined}
          />
        )}
      </div>
    </div>
  )
}
